import classes from './ChatReplyTemplateMenu.module.scss';
import {
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import type { ReactNode, Ref, SyntheticEvent } from 'react';
import { ContextMenu } from 'primereact/contextmenu';
import type { MenuItem } from 'primereact/menuitem';
import { toast } from 'sonner';
import type { ChatReplyFolderResponse } from '@/core/generated/api';
import { useChatReplyTemplates } from '@/entities/ChatReplyTemplate/model/store';
import { canAccessPermission, useAdminAccess } from '@/shared/model/adminAccess';
import { CONTEXT_MENU_PT } from '@/shared/ui/RowContextMenu/contextMenuConfig';
import { getContextMenuEvent } from '@/shared/ui/RowContextMenu/contextMenuEvent';
import type { ChatReplyTemplateMenuHandle } from './types';

interface ChatReplyTemplateMenuProps {
    children: ReactNode;
    triggerRef?: Ref<ChatReplyTemplateMenuHandle>;
    disabled?: boolean;
    maxLength: number;
    onInsert: (text: string) => void;
}

/** Вставляет шаблон из дерева папок в поле ввода сообщения */
export const ChatReplyTemplateMenu = (
    { children, triggerRef, disabled, maxLength, onInsert }: ChatReplyTemplateMenuProps
) => {
    const menu = useRef<ContextMenu>(null);
    const container = useRef<HTMLDivElement>(null);
    const editor = useRef<HTMLTextAreaElement | null>(null);

    const selection = useRef({
        start: 0,
        end: 0
    });

    const revision = useRef(0);
    const retryTimer = useRef<ReturnType<typeof setTimeout>>();
    const [items, setItems] = useState<MenuItem[]>([]);
    const snapshot = useAdminAccess(state => state.snapshot);
    const allowed = canAccessPermission(snapshot, 'admin.chat-replies.view');

    useEffect(() => () => {
        revision.current += 1;
        clearTimeout(retryTimer.current);
    }, []);

    /** Открывает дерево шаблонов в свободной области переписки */
    const show = (event: SyntheticEvent): void => {
        const target = container.current?.querySelector('textarea');

        if (disabled || !allowed || !target || target.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        editor.current = target;
        selection.current = {
            start: target.selectionStart,
            end: target.selectionEnd
        };
        const position = getContextMenuEvent(event);
        const request = ++revision.current;

        setItems([{
            label: 'Загрузка шаблонов...',
            disabled: true,
            icon: 'pi pi-spin pi-spinner'
        }]);

        menu.current?.show(position);

        /** Загружает папки и сообщения для контекстного меню */
        const load = async (): Promise<void> => {
            await useChatReplyTemplates.getState().load(true);

            if (request !== revision.current) return;

            const { catalog, error } = useChatReplyTemplates.getState();

            if (error) {
                setItems([{
                    label: 'Не удалось загрузить шаблоны. Повторить',
                    icon: 'pi pi-refresh',
                    command: () => {
                        retryTimer.current = setTimeout(() => {
                            menu.current?.show(position);
                            void load();
                        }, 0);
                    },
                }]);

                return;
            }

            /** Собирает сообщения и подпапки выбранной папки */
            const folderItem = (folder: ChatReplyFolderResponse): MenuItem => {
                const messages: MenuItem[] = (catalog?.templates ?? [])
                    .filter(template => template.folderId === folder.id)
                    .map(template => ({
                        label: template.name ?? 'Без названия',
                        icon: 'pi pi-comment',
                        command: () => {
                            const canInsert = !disabled && canAccessPermission(useAdminAccess.getState().snapshot, 'admin.chat-replies.view');
                            const textarea = editor.current;

                            if (!canInsert || !textarea || textarea.disabled) return;

                            const { start, end } = selection.current;
                            const text = template.text ?? '';
                            const next = textarea.value.slice(0, start) + text + textarea.value.slice(end);

                            if (next.length > maxLength) {
                                toast.error(`Сообщение должно содержать не больше ${maxLength} символов`);

                                return;
                            }

                            onInsert(next);

                            requestAnimationFrame(() => {
                                textarea.focus();
                                textarea.setSelectionRange(start + text.length, start + text.length);
                            });
                        },
                    }));

                const children = (catalog?.folders ?? [])
                    .filter(child => child.parentId === folder.id)
                    .map(child => ({
                        label: child.name ?? 'Без названия',
                        icon: 'pi pi-folder',
                        items: (catalog?.templates ?? [])
                            .filter(template => template.folderId === child.id)
                            .length > 0
                            ? folderItem(child).items
                            : [{
                                label: 'Папка пуста',
                                disabled: true
                            }],
                    }));

                return {
                    label: folder.name ?? 'Без названия',
                    icon: 'pi pi-folder',
                    items: [...children, ...messages].length > 0
                        ? [...children, ...messages]
                        : [{
                            label: 'Папка пуста',
                            disabled: true
                        }],
                };
            };

            const roots = (catalog?.folders ?? []).filter(folder => !folder.parentId).map(folderItem);

            setItems(roots.length > 0
                ? roots
                : [{
                    label: 'Шаблоны ещё не созданы',
                    disabled: true
                }]);
        };

        void load();
    };

    useImperativeHandle(triggerRef, () => ({ show }));

    if (!allowed) return children;

    return <div ref={container}>
        {children}
        <ContextMenu
            ref={menu}
            className={classes.menu}
            pt={CONTEXT_MENU_PT}
            model={items}
            ariaLabel="Шаблоны сообщений чата"
        />
    </div>;
};
